
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Problem {
  num1: number;
  num2: number;
  operator: string;
  answer: number;
}

const QuickMath = () => {
  const [score, setScore] = useState<number>(0);
  const [timeLeft, setTimeLeft] = useState<number>(30);
  const [gameActive, setGameActive] = useState<boolean>(false);
  const [currentProblem, setCurrentProblem] = useState<Problem | null>(null);
  const [userAnswer, setUserAnswer] = useState<string>('');
  const [streak, setStreak] = useState<number>(0);
  const { toast } = useToast();
  
  const generateProblem = () => {
    const operators = ['+', '-', '*'];
    const operatorIndex = Math.floor(Math.random() * operators.length);
    const operator = operators[operatorIndex];
    
    let num1, num2, answer;
    
    switch (operator) {
      case '+':
        num1 = Math.floor(Math.random() * 20) + 1;
        num2 = Math.floor(Math.random() * 20) + 1;
        answer = num1 + num2;
        break;
      case '-':
        num1 = Math.floor(Math.random() * 20) + 1;
        num2 = Math.floor(Math.random() * num1) + 1; // Ensure positive result
        answer = num1 - num2;
        break;
      case '*':
        num1 = Math.floor(Math.random() * 10) + 1;
        num2 = Math.floor(Math.random() * 10) + 1;
        answer = num1 * num2;
        break;
      default:
        num1 = 1;
        num2 = 1;
        answer = 2;
    }
    
    setCurrentProblem({ num1, num2, operator, answer });
  };
  
  const startGame = () => {
    setScore(0);
    setStreak(0);
    setTimeLeft(30);
    setGameActive(true);
    setUserAnswer('');
    generateProblem();
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentProblem) return;
    
    const parsedAnswer = parseInt(userAnswer, 10);
    if (isNaN(parsedAnswer)) {
      toast({
        title: "Invalid Answer",
        description: "Please enter a valid number",
        variant: "destructive"
      });
      return;
    }
    
    if (parsedAnswer === currentProblem.answer) {
      // Correct answer
      const newStreak = streak + 1;
      setStreak(newStreak);
      
      // Bonus points for streaks
      const pointsToAdd = Math.min(newStreak, 5);
      setScore(prev => prev + pointsToAdd);
      
      toast({
        title: "Correct!",
        description: `+${pointsToAdd} points${newStreak > 1 ? ` (${newStreak}x streak!)` : ''}`,
        variant: "default"
      });
      
      // Generate new problem
      setUserAnswer('');
      generateProblem();
      
      // Add time as reward
      setTimeLeft(prev => Math.min(prev + 2, 60));
    } else {
      // Wrong answer
      setStreak(0);
      toast({
        title: "Incorrect",
        description: `The answer was ${currentProblem.answer}`,
        variant: "destructive"
      });
      setUserAnswer('');
      generateProblem();
    }
  };
  
  // Timer
  useEffect(() => {
    let timer: number | undefined;
    
    if (gameActive && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000) as unknown as number;
    } else if (timeLeft === 0 && gameActive) {
      setGameActive(false);
      toast({
        title: "Game Over!",
        description: `Your final score is ${score}`,
      });
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [gameActive, timeLeft, score, toast]);
  
  const getOperatorSymbol = (op: string) => {
    switch (op) {
      case '+': return '+';
      case '-': return '−';
      case '*': return '×';
      default: return op;
    }
  };
  
  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <Badge variant={timeLeft < 10 ? "destructive" : "outline"} className="flex items-center gap-1">
          <Clock className="h-3 w-3" /> {timeLeft}s
        </Badge>
        <Badge variant="outline">Score: {score}</Badge>
      </div>
      
      {!gameActive ? (
        <div className="text-center py-6">
          <h3 className="font-medium mb-2">Quick Math Challenge</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Solve math problems before time runs out. Each correct answer adds time!
          </p>
          <Button onClick={startGame}>Start Game</Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-muted/50 p-6 rounded-lg text-center">
            <div className="text-2xl font-bold flex items-center justify-center gap-3">
              <span>{currentProblem?.num1}</span>
              <span>{getOperatorSymbol(currentProblem?.operator || '+')}</span>
              <span>{currentProblem?.num2}</span>
              <span>=</span>
              <span>?</span>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            <Input
              type="number"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              placeholder="Your answer..."
              className="flex-1"
              autoFocus
            />
            <Button type="submit">Submit</Button>
          </form>
          
          {streak > 1 && (
            <Badge className="bg-amber-500 hover:bg-amber-600">
              {streak}x Streak!
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

export default QuickMath;
